'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSelector } from 'react-redux';
import { RootState } from '@/app/store';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AppWindow as AppIcon, ExternalLink, Lock } from 'lucide-react';
import { buttonStyles, iconSizes } from '@/app/constants/themeConstants';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { useAppDescription } from '@/app/hooks/useAppDescription';
import { sysAppService, SysAppDto } from '@/app/services/api/sysAppService';
import { toShellRoute } from '@/app/features/workspace/utils/shellRoute';
import { workspaceHref } from '@/app/features/workspace/utils/workspaceRoute';

const MAX_SHOWCASE = 3;

const PublicAppShowcase = React.memo(function PublicAppShowcase() {
  const router = useRouter();
  const { t } = useTranslation('publicApps');
  const { getAppText } = useAppDescription();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const activeTenant = useSelector((state: RootState) => state.tenant.activeTenant);
  const [apps, setApps] = useState<SysAppDto[]>([]);

  // Fetch apps from API — sorted by sortOrder, take first 3
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await sysAppService.getAvailableApps();
        if (!cancelled) {
          const sorted = all
            .filter(a => a.isActive && (a.classification === 'app' || a.classification === 'tool'))
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .slice(0, MAX_SHOWCASE);
          setApps(sorted);
        }
      } catch {
        // API may fail for unauthenticated users — that's OK, show nothing
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAppClick = useCallback(
    (app: SysAppDto) => {
      // Route into the shell that matches the user's current context:
      //   active tenant selected → /workspace (tenant-scoped shell)
      //   no active tenant       → /main (guest / public shell)
      const basePath = activeTenant ? workspaceHref(activeTenant) : '/main';
      const target = toShellRoute(app, basePath);
      if (app.accessType === 'public' || isAuthenticated) {
        router.push(target);
      } else {
        router.push(`/login?returnUrl=${encodeURIComponent(target)}`);
      }
    },
    [router, isAuthenticated, activeTenant]
  );

  if (apps.length === 0) return null;

  return (
    <div className="py-24 bg-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-foreground">
          {t('sectionTitle')}
        </h2>
        <p className="text-lg text-center mb-12 text-muted-foreground max-w-2xl mx-auto">
          {t('sectionSubtitle')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((app) => {
            const needsAuth = app.accessType !== 'public' && !isAuthenticated;
            return (
              <Card
                key={app.id}
                className="h-full border-2 border-border/50 rounded-2xl bg-gradient-to-br from-background/90 to-background/70 backdrop-blur-sm shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
              >
                <CardContent className="p-8 text-center flex flex-col h-full">
                  <div className="mb-4 flex items-center justify-center">
                    <AppIcon className={`${iconSizes.large} text-primary`} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">
                    {getAppText(app.appCode, app.appName, app.description, app.id).name}
                  </h3>
                  {getAppText(app.appCode, app.appName, app.description, app.id).description ? (
                    <p className="text-muted-foreground mb-6 leading-relaxed text-sm flex-1">
                      {getAppText(app.appCode, app.appName, app.description, app.id).description}
                    </p>
                  ) : (
                    <div className="flex-1" />
                  )}
                  <Button
                    className={buttonStyles.primary}
                    variant="default"
                    onClick={() => handleAppClick(app)}
                  >
                    {needsAuth ? (
                      <>
                        <Lock className={`${iconSizes.button} mr-2`} />
                        {t('tryButton')}
                      </>
                    ) : (
                      <>
                        <ExternalLink className={`${iconSizes.button} mr-2`} />
                        {t('openButton')}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
});

PublicAppShowcase.displayName = 'PublicAppShowcase';
export default PublicAppShowcase;
